/**
 * PriceChart — daily OHLCV candlestick chart with SMA-20/50 overlays and
 * a volume sub-panel.
 *
 * Phase 1 / G6. Displayed on the Company page, above the fundamentals panel.
 * Uses Plotly (already in the bundle via react-plotly.js) so no new dep is
 * needed. Indicators are computed client-side from the raw OHLCV data.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPrices } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { EmptyState, LoadingState } from "@/components/ui/States";
import { Plot, PLOTLY_CONFIG_BASE, PLOTLY_LAYOUT_BASE } from "./plot";
import type { OHLCVBar } from "@/lib/api/types";
import { clsx } from "clsx";

type Props = {
  securityId: number;
  /** Used in the panel title only. */
  ticker?: string;
  height?: number;
};

// ---------------------------------------------------------------------------
// Period presets
// ---------------------------------------------------------------------------

const PERIODS = [
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
  { label: "2Y", days: 730 },
  { label: "5Y", days: 1825 },
] as const;

type PeriodLabel = (typeof PERIODS)[number]["label"];

function isoDateDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Technical indicator helpers (client-side)
// ---------------------------------------------------------------------------

/**
 * Simple moving average — returns null for positions before the window fills.
 */
function computeSMA(values: number[], period: number): (number | null)[] {
  let sum = 0;
  return values.map((v, i) => {
    sum += v;
    if (i >= period) sum -= values[i - period]!;
    if (i < period - 1) return null;
    return sum / period;
  });
}

// ---------------------------------------------------------------------------
// Plotly sub-component
// ---------------------------------------------------------------------------

function PricePlot({ bars, height }: { bars: OHLCVBar[]; height: number }) {
  const dates = bars.map((b) => b.date);
  const opens = bars.map((b) => b.open);
  const highs = bars.map((b) => b.high);
  const lows = bars.map((b) => b.low);
  const closes = bars.map((b) => b.close);
  const volumes = bars.map((b) => b.volume ?? 0);

  const sma20 = computeSMA(closes, 20);
  const sma50 = computeSMA(closes, 50);

  // Volume bar colour mirrors candle direction.
  const volColors = bars.map((b) =>
    b.close >= b.open ? "rgba(16,185,129,0.55)" : "rgba(239,68,68,0.55)",
  );

  return (
    <Plot
      data={
        [
          // ── Main panel: candlestick ──────────────────────────────────────
          {
            type: "candlestick",
            x: dates,
            open: opens,
            high: highs,
            low: lows,
            close: closes,
            increasing: { line: { color: "#10b981", width: 1 }, fillcolor: "#10b981" },
            decreasing: { line: { color: "#ef4444", width: 1 }, fillcolor: "#ef4444" },
            yaxis: "y",
            xaxis: "x",
            name: "OHLCV",
            hoverinfo: "x+open+high+low+close",
            showlegend: false,
          },
          // ── SMA 20 ───────────────────────────────────────────────────────
          {
            type: "scatter",
            mode: "lines",
            x: dates,
            y: sma20,
            line: { color: "#f59e0b", width: 1.5, dash: "solid" },
            yaxis: "y",
            xaxis: "x",
            name: "SMA 20",
            hovertemplate: "SMA 20: %{y:.2f}<extra></extra>",
          },
          // ── SMA 50 ───────────────────────────────────────────────────────
          {
            type: "scatter",
            mode: "lines",
            x: dates,
            y: sma50,
            line: { color: "#a855f7", width: 1.5, dash: "solid" },
            yaxis: "y",
            xaxis: "x",
            name: "SMA 50",
            hovertemplate: "SMA 50: %{y:.2f}<extra></extra>",
          },
          // ── Volume sub-panel ─────────────────────────────────────────────
          {
            type: "bar",
            x: dates,
            y: volumes,
            marker: { color: volColors },
            yaxis: "y2",
            xaxis: "x",
            name: "Volume",
            hovertemplate: "Volume: %{y:,.0f}<extra></extra>",
            showlegend: false,
          },
        ] as never[]
      }
      layout={{
        ...PLOTLY_LAYOUT_BASE,
        height,
        margin: { t: 8, r: 20, b: 32, l: 64 },
        showlegend: true,
        legend: {
          orientation: "h",
          x: 0,
          y: 1.05,
          font: { size: 10, color: "#9aa3ad" },
          bgcolor: "transparent",
        },
        // Main price panel occupies the top 73 % of the chart area.
        yaxis: {
          ...PLOTLY_LAYOUT_BASE.yaxis,
          domain: [0.30, 1.0],
          autorange: true,
          fixedrange: false,
        },
        // Volume sub-panel below, separated by a small gap.
        yaxis2: {
          ...PLOTLY_LAYOUT_BASE.yaxis,
          domain: [0.0, 0.26],
          showgrid: false,
          showticklabels: false,
          fixedrange: true,
        },
        xaxis: {
          ...PLOTLY_LAYOUT_BASE.xaxis,
          type: "date",
          rangeslider: { visible: false },
          fixedrange: false,
        },
      }}
      config={{
        ...PLOTLY_CONFIG_BASE,
        displayModeBar: true,
        modeBarButtonsToRemove: ["lasso2d", "select2d", "autoScale2d"],
      }}
      style={{ width: "100%" }}
    />
  );
}

// ---------------------------------------------------------------------------
// Panel wrapper
// ---------------------------------------------------------------------------

export function PriceChart({ securityId, ticker, height = 480 }: Props) {
  const [period, setPeriod] = useState<PeriodLabel>("1Y");

  const days = PERIODS.find((p) => p.label === period)?.days ?? 365;

  const query = useQuery({
    queryKey: ["prices", securityId, period],
    queryFn: () =>
      getPrices(securityId, {
        fromDate: isoDateDaysAgo(days),
        adjusted: true,
        limit: 2000,
      }),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const periodSelector = (
    <div className="flex gap-0.5">
      {PERIODS.map((p) => (
        <button
          key={p.label}
          onClick={() => setPeriod(p.label)}
          className={clsx(
            "rounded px-2 py-0.5 text-xs transition-colors",
            p.label === period
              ? "bg-accent text-white"
              : "text-foreground-secondary hover:text-foreground-primary",
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  return (
    <Panel
      title={ticker ? `Price — ${ticker}` : "Price"}
      description="Adjusted close · SMA 20 &amp; 50 overlays · volume"
      action={periodSelector}
    >
      {query.isLoading && <LoadingState />}
      {query.isError && (
        <EmptyState title="Price history not available" description="Ingest OHLCV data first." />
      )}
      {!query.isLoading && !query.isError && query.data?.length === 0 && (
        <EmptyState title="No price data for this period." />
      )}
      {query.data && query.data.length > 0 && (
        <PricePlot bars={query.data} height={height} />
      )}
    </Panel>
  );
}
